/**
 * Encoding / decoding library for the XOR Anything tool.
 *
 * Supports decoding from and encoding to many formats:
 *   hex, binary, UTF-8 text, Base64, decimal integer,
 *   Nostr nsec, Bitcoin WIF, BIP-39 mnemonic, RGB color, raw bytes
 *
 * All decode functions return a `Uint8Array` (big-endian byte sequence).
 * All encode functions accept a `Uint8Array` and return a string.
 * Encode functions that only apply to certain byte lengths return `null`
 * when the length doesn't match (e.g. nsec requires exactly 32 bytes).
 */

import { nip19 } from "nostr-tools";
import { base58check } from "@scure/base";
import { entropyToMnemonic, mnemonicToEntropy } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";

// ─── types ────────────────────────────────────────────────────────────

export type InputFormatId =
  | "hex"
  | "binary"
  | "utf8"
  | "base64"
  | "decimal"
  | "nsec"
  | "wif"
  | "mnemonic"
  | "rgb"
  | "bytes";

export interface InputFormat {
  id: InputFormatId;
  label: string;
  /** Short hint shown as placeholder */
  hint: string;
  /** Decode a user-entered string into bytes */
  decode: (input: string) => Uint8Array;
  /** Encode bytes back to this format's string. Returns null if not applicable. */
  encode: (bytes: Uint8Array) => string | null;
}

// ─── hex ──────────────────────────────────────────────────────────────

function decodeHex(input: string): Uint8Array {
  const cleaned = input.replace(/^0x/i, "").replace(/[\s_-]/g, "");
  if (cleaned.length === 0) return new Uint8Array(0);
  if (cleaned.length % 2 !== 0) {
    throw new Error("Hex string must have an even number of digits");
  }
  if (!/^[0-9a-fA-F]+$/.test(cleaned)) {
    throw new Error("Invalid hex characters — use 0-9 and a-f only");
  }
  const bytes = new Uint8Array(cleaned.length / 2);
  for (let i = 0; i < cleaned.length; i += 2) {
    bytes[i / 2] = parseInt(cleaned.slice(i, i + 2), 16);
  }
  return bytes;
}

function encodeHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─── binary ───────────────────────────────────────────────────────────

function decodeBinary(input: string): Uint8Array {
  let cleaned = input.replace(/^0b/i, "").replace(/[\s_]/g, "");
  if (cleaned.length === 0) return new Uint8Array(0);
  if (!/^[01]+$/.test(cleaned)) {
    throw new Error("Binary string must contain only 0s and 1s");
  }
  // Pad to a multiple of 8
  const pad = (8 - (cleaned.length % 8)) % 8;
  if (pad > 0) cleaned = "0".repeat(pad) + cleaned;
  const bytes = new Uint8Array(cleaned.length / 8);
  for (let i = 0; i < cleaned.length; i += 8) {
    bytes[i / 8] = parseInt(cleaned.slice(i, i + 8), 2);
  }
  return bytes;
}

function encodeBinary(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(2).padStart(8, "0"))
    .join(" ");
}

// ─── UTF-8 text ───────────────────────────────────────────────────────

function decodeUtf8(input: string): Uint8Array {
  return new TextEncoder().encode(input);
}

function encodeUtf8(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

// ─── base64 ───────────────────────────────────────────────────────────

function decodeBase64(input: string): Uint8Array {
  const cleaned = input.replace(/\s/g, "");
  if (cleaned.length === 0) return new Uint8Array(0);
  // Convert base64 string to binary string then to bytes
  const binary = atob(cleaned);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function encodeBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary);
}

// ─── decimal (big integer) ───────────────────────────────────────────

function decodeDecimal(input: string): Uint8Array {
  const cleaned = input.trim().replace(/[\s_,]/g, "");
  if (cleaned.length === 0) return new Uint8Array(0);
  let big: bigint;
  try {
    big = BigInt(cleaned);
  } catch {
    throw new Error("Invalid decimal integer");
  }
  if (big < 0n) throw new Error("Negative values are not supported");
  if (big === 0n) return new Uint8Array(1);
  const bytes: number[] = [];
  while (big > 0n) {
    bytes.unshift(Number(big & 0xffn));
    big >>= 8n;
  }
  return new Uint8Array(bytes);
}

function encodeDecimal(bytes: Uint8Array): string {
  let result = 0n;
  for (const b of bytes) {
    result = (result << 8n) | BigInt(b);
  }
  return result.toString();
}

// ─── Nostr nsec ───────────────────────────────────────────────────────

function decodeNsec(input: string): Uint8Array {
  const cleaned = input.trim();
  if (cleaned.length === 0) return new Uint8Array(0);
  try {
    const decoded = nip19.decode(cleaned);
    if (decoded.type !== "nsec") {
      throw new Error(`Expected nsec, got ${decoded.type}`);
    }
    // decoded.data is a Uint8Array for nsec in nostr-tools v2
    const data = decoded.data;
    if (data instanceof Uint8Array) return data;
    // Fallback for versions that return hex strings
    if (typeof data === "string") return hexToBytes(data);
    throw new Error("Unexpected nsec data type");
  } catch (e) {
    throw new Error(
      `Invalid nsec: ${e instanceof Error ? e.message : "decode failed"}`,
    );
  }
}

function encodeNsec(bytes: Uint8Array): string | null {
  if (bytes.length !== 32) return null;
  return nip19.nsecEncode(bytes);
}

// ─── Bitcoin WIF ──────────────────────────────────────────────────────

/**
 * Decode a Bitcoin Wallet Import Format (WIF) private key.
 *
 * WIF = Base58Check( 0x80 || 32-byte-key || [0x01 if compressed] )
 * Returns just the 32-byte private key.
 */
function decodeWif(input: string): Uint8Array {
  const cleaned = input.trim();
  if (cleaned.length === 0) return new Uint8Array(0);
  let decoded: Uint8Array;
  try {
    decoded = base58check.decode(cleaned);
  } catch (e) {
    throw new Error(
      `Invalid WIF: ${e instanceof Error ? e.message : "decode failed"}`,
    );
  }
  // Mainnet WIF starts with 0x80
  if (decoded[0] !== 0x80) {
    // Testnet WIF starts with 0xef
    if (decoded[0] !== 0xef) {
      throw new Error("Invalid WIF version byte (not mainnet or testnet)");
    }
  }
  // Key is 32 bytes after version byte, optionally followed by 0x01 (compressed)
  if (decoded.length === 33) {
    return decoded.slice(1); // uncompressed
  }
  if (decoded.length === 34 && decoded[33] === 0x01) {
    return decoded.slice(1, 33); // compressed
  }
  throw new Error(
    `Invalid WIF length: expected 33 or 34 bytes, got ${decoded.length}`,
  );
}

/**
 * Encode 32 bytes as a compressed mainnet WIF private key.
 */
function encodeWif(bytes: Uint8Array): string | null {
  if (bytes.length !== 32) return null;
  const payload = new Uint8Array(34);
  payload[0] = 0x80; // mainnet
  payload.set(bytes, 1);
  payload[33] = 0x01; // compressed
  return base58check.encode(payload);
}

// ─── BIP-39 mnemonic ──────────────────────────────────────────────────

/** Valid entropy byte lengths for BIP-39 (16/20/24/28/32). */
const VALID_ENTROPY_LENGTHS = [16, 20, 24, 28, 32];

function decodeMnemonic(input: string): Uint8Array {
  const cleaned = input.trim().toLowerCase().replace(/\s+/g, " ");
  if (cleaned.length === 0) return new Uint8Array(0);
  try {
    return mnemonicToEntropy(cleaned, wordlist);
  } catch (e) {
    throw new Error(
      `Invalid BIP-39 mnemonic: ${e instanceof Error ? e.message : "decode failed"}`,
    );
  }
}

function encodeMnemonic(bytes: Uint8Array): string | null {
  if (!VALID_ENTROPY_LENGTHS.includes(bytes.length)) return null;
  try {
    return entropyToMnemonic(bytes, wordlist);
  } catch {
    return null;
  }
}

// ─── RGB color ────────────────────────────────────────────────────────

function decodeRgb(input: string): Uint8Array {
  const cleaned = input.trim();
  if (cleaned.length === 0) return new Uint8Array(0);

  // #RRGGBB
  let m = cleaned.match(/^#?([0-9a-fA-F]{6})$/);
  if (m) {
    const hex = m[1];
    return Uint8Array.from([
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ]);
  }

  // #RGB (short form)
  m = cleaned.match(/^#?([0-9a-fA-F]{3})$/);
  if (m) {
    const h = m[1];
    return Uint8Array.from([
      parseInt(h[0] + h[0], 16),
      parseInt(h[1] + h[1], 16),
      parseInt(h[2] + h[2], 16),
    ]);
  }

  // rgb(r, g, b)
  m = cleaned.match(/^rgba?\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)/i);
  if (m) {
    const [r, g, b] = [+m[1], +m[2], +m[3]];
    if (r > 255 || g > 255 || b > 255) {
      throw new Error("RGB values must be 0–255");
    }
    return Uint8Array.from([r, g, b]);
  }

  throw new Error("Invalid color format — use #RRGGBB, #RGB, or rgb(r, g, b)");
}

function encodeRgb(bytes: Uint8Array): string | null {
  if (bytes.length !== 3) return null;
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `#${hex}`;
}

// ─── raw bytes (comma/space separated decimal) ────────────────────────

function decodeBytes(input: string): Uint8Array {
  const parts = input
    .trim()
    .split(/[\s,;]+/)
    .filter((p) => p.length > 0);
  if (parts.length === 0) return new Uint8Array(0);
  const bytes: number[] = [];
  for (const part of parts) {
    const n = parseInt(part, 10);
    if (isNaN(n) || n < 0 || n > 255) {
      throw new Error(`Invalid byte value: "${part}" (must be 0–255)`);
    }
    bytes.push(n);
  }
  return new Uint8Array(bytes);
}

function encodeBytes(bytes: Uint8Array): string {
  return Array.from(bytes).join(", ");
}

// ─── format registry ──────────────────────────────────────────────────

export const INPUT_FORMATS: InputFormat[] = [
  { id: "hex", label: "Hex", hint: "a1b2c3d4 or 0xa1b2…", decode: decodeHex, encode: encodeHex },
  { id: "binary", label: "Binary", hint: "10100001 10110010", decode: decodeBinary, encode: encodeBinary },
  { id: "utf8", label: "UTF-8 Text", hint: "Hello, world!", decode: decodeUtf8, encode: encodeUtf8 },
  { id: "base64", label: "Base64", hint: "oWLFxA==", decode: decodeBase64, encode: encodeBase64 },
  { id: "decimal", label: "Decimal", hint: "123456789", decode: decodeDecimal, encode: encodeDecimal },
  { id: "nsec", label: "Nostr nsec", hint: "nsec1…", decode: decodeNsec, encode: encodeNsec },
  { id: "wif", label: "Bitcoin WIF", hint: "5K… or Kx…", decode: decodeWif, encode: encodeWif },
  { id: "mnemonic", label: "BIP-39 Mnemonic", hint: "abandon abandon abandon…", decode: decodeMnemonic, encode: encodeMnemonic },
  { id: "rgb", label: "RGB Color", hint: "#ff0088 or rgb(255,0,136)", decode: decodeRgb, encode: encodeRgb },
  { id: "bytes", label: "Raw Bytes", hint: "161, 178, 195, 212", decode: decodeBytes, encode: encodeBytes },
];

export const FORMAT_MAP = Object.fromEntries(
  INPUT_FORMATS.map((f) => [f.id, f]),
) as Record<InputFormatId, InputFormat>;

// ─── utility ──────────────────────────────────────────────────────────

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

/** Encode bytes into all applicable formats for the output panel. */
export interface EncodedOutput {
  formatId: InputFormatId;
  label: string;
  value: string;
}

export function encodeAll(bytes: Uint8Array): EncodedOutput[] {
  const outputs: EncodedOutput[] = [];
  for (const fmt of INPUT_FORMATS) {
    try {
      const value = fmt.encode(bytes);
      if (value !== null && value.length > 0) {
        outputs.push({ formatId: fmt.id, label: fmt.label, value });
      }
    } catch {
      // Skip formats that fail to encode (e.g. invalid UTF-8, bad nsec)
    }
  }
  return outputs;
}
